import React, { useEffect, useMemo, useState } from "react";
import LayoutWrapper from "../../../components/LayoutWrapper";
import HeaderBar from "../../../components/HeaderBar";
import { FaSearch } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Entry page (ASPX: UnderMaintenanceSparesEntry.aspx)
 * APIs:
 *  GET  /api/underSpare/underMaintenance_generateNumber?branchId=&selOption=
 *  GET  /api/underSpare/um_suppliers?branchId=
 *  POST /api/underSpare/underMaintenance_submit
 *
 * BUGS FIXED (original code had these issues):
 *  1. payload.SelOption was `item.fld_itemid` (undefined `item` reference) → fixed to `source`
 *  2. itemRows used outer JSX `item` variable (only exists in render scope) → fixed to use `chosen` map var
 *  3. `checked: true` was missing from each itemRow → backend checkedRows filter returned empty
 *  4. `availableQty` was missing → backend qty ≤ available validation was always skipped
 *  5. `autoNumber` was missing from payload → master SP received 0
 *  6. location field was hardcoded "" → now uses location_ state for "Own Work Shop"
 */
const UnderMaintenanceSparesEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { selectedItems = [], source = "Supplier", branchId = 8 } = location.state || {};

  // If user refreshes entry page (state lost), go back safely
  useEffect(() => {
    if (!selectedItems || selectedItems.length === 0) {
      toast.info("No items received. Please select items from listing.");
      navigate("/inventory/UnderMaintenanceSpares", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [number, setNumber] = useState("");
  const [dcNumber, setDcNumber] = useState("");
  const [date, setDate] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [suppliersList, setSuppliersList] = useState([]);

  // FIX 6: location field for "Own Work Shop" option (was hardcoded "" in payload)
  const [location_, setLocation_] = useState("");

  // which rows user wants to submit (second-level selection like ASPX checkbox grid)
  const [selected, setSelected] = useState({});
  const [remarksMap, setRemarksMap] = useState({});
  const [removedFromMap, setRemovedFromMap] = useState({});

  // FIX 4 & 5: qtyMap so user can edit UM Qty per row (mirrors ASPX txt_Qty)
  const [qtyMap, setQtyMap] = useState(() => {
    const map = {};
    selectedItems.forEach((r) => {
      if (r?.fld_itemid != null) map[r.fld_itemid] = r.fld_qty ?? r.qty ?? "";
    });
    return map;
  });

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected]
  );

  // Load auto number
  useEffect(() => {
    if (!branchId) return;

    axios
      .get(`https://nissi-india.com/api/underSpare/underMaintenance_generateNumber?branchId=${branchId}&selOption=${encodeURIComponent(source)}`)
      .then((res) => {
        setNumber(res.data?.code || "");
      })
      .catch(() => toast.error("Failed to fetch Number"));
  }, [branchId, source]);

  // Load suppliers (optional)
  useEffect(() => {
    axios
      .get(`https://nissi-india.com/api/underSpare/um_suppliers?branchId=${branchId}`)
      .then((res) => {
        setSuppliersList(res.data?.data || []);
      })
      .catch(() => {
        // not mandatory; ignore if endpoint not wired
      });
  }, [branchId]);

  const toggleRow = (itemId) => {
    setSelected((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const selectAll = (checked) => {
    if (!checked) return setSelected({});
    const map = {};
    selectedItems.forEach((r) => {
      if (r?.fld_itemid != null) map[r.fld_itemid] = true;
    });
    setSelected(map);
  };

  const handleRemarksChange = (id, value) => setRemarksMap((p) => ({ ...p, [id]: value }));
  const handleRemovedFromChange = (id, value) => setRemovedFromMap((p) => ({ ...p, [id]: value }));

  // FIX 4: UM Qty handler — numeric only, mirrors ASPX FilteredTextBoxExtender
  const handleQtyChange = (id, value) => {
    if (value !== "" && !/^\d+$/.test(value)) return;
    setQtyMap((p) => ({ ...p, [id]: value }));
  };

  const handleAdd = async () => {
    const chosen = selectedItems.filter((r) => selected[r.fld_itemid]);

    // Client-side validations
    if (chosen.length === 0) { toast.warning("Please select at least one row to submit."); return; }
    if (source === "Supplier" && (!supplierId || supplierId === "Select")) { toast.warning("Please select Supplier."); return; }
    if (source === "Own Work Shop" && !location_.trim()) { toast.warning("Please enter Location."); return; }
    if (!dcNumber) { toast.warning("Please enter DC Number."); return; }
    if (!date) { toast.warning("Please select Date."); return; }

    for (const item of chosen) {
      const umQty    = Number(qtyMap[item.fld_itemid] ?? 0);
      const availQty = Number(item.fld_qty ?? item.qty ?? 0);
      if (!umQty || umQty <= 0) { toast.warning(`Please enter UM Qty for "${item.fld_itemname}".`); return; }
      if (availQty && umQty > availQty) { toast.warning(`UM Qty cannot exceed available Qty (${availQty}) for "${item.fld_itemname}".`); return; }
    }

    try {
      // Step 1: Fetch storesIssueId + storesIssueDetId for each chosen item
      // Required because tbl_UnderMaintenanceSparesDet has FK on fld_storesissueid
      // Backend queries tbl_storesissue_det JOIN tbl_storesissue_mas by branchId + itemIds
      const itemIds = chosen.map((r) => r.fld_itemid).join(",");
      let storesIssueMap = {};
      try {
        const issueRes = await axios.get(
          `https://nissi-india.com/api/underSpare/um_storesIssueIds?branchId=${branchId}&itemIds=${itemIds}`
        );
        storesIssueMap = issueRes.data?.data || {};
      } catch {
        storesIssueMap = {};
      }

      // Step 2: Build payload with resolved storesIssueId + storesIssueDetId
      const payload = {
        SelOption: source,
        branchId,
        userId: 37,
        dcNo: dcNumber,
        dcDate: date,
        location: source === "Own Work Shop" ? location_.trim() : "",
        autoNumber: Number(number) || 0,
        flag: "false",
        SelId: null,
        supplierId: source === "Supplier" ? supplierId : "",
        itemRows: chosen.map((item) => {
          const issueData = storesIssueMap[item.fld_itemid] || {};
          return {
            itemId:          item.fld_itemid,
            storesIssueId:    issueData.storesIssueId    ?? item.fld_storesissueid    ?? null,
            storesIssueDetId: issueData.storesIssueDetId ?? item.fld_storesissuedetid ?? null,
            qty:          Number(qtyMap[item.fld_itemid] ?? item.fld_qty ?? item.qty ?? 1),
            availableQty: Number(item.fld_qty ?? item.qty ?? 0),
            remarks:      remarksMap[item.fld_itemid]     || "",
            removedFrom:  removedFromMap[item.fld_itemid] || "",
            removedate:   item.fld_removedate || "",
            checked:      true,
          };
        }),
      };

      // Step 3: Submit
      const res = await axios.post(
        "https://nissi-india.com/api/underSpare/underMaintenance_submit",
        payload
      );
      toast.success(res.data?.message || "Submitted successfully");
      navigate("/inventory/UnderMaintenanceSpares", { replace: true });
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || "Failed to submit data";
      toast.error(msg);
    }
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="UNDER MAINTENANCE SPARES ENTRY" />
      <ToastContainer />

      <div className="p-6 mt-6 rounded-2xl shadow-xl border bg-white text-black">
        {/* Header line */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-lg font-semibold">Entry Mode: {source}</div>
            <div className="text-sm text-gray-600">
              Items received: {selectedItems.length}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={selectedCount === selectedItems.length && selectedItems.length > 0}
              onChange={(e) => selectAll(e.target.checked)}
            />
            Select All
          </label>
        </div>

        {/* Filters / header fields */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-4">
          <div>
            <label className="block text-sm font-semibold">Number</label>
            <input
              type="text"
              value={number}
              readOnly
              className="w-full border px-2 py-2 rounded bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold">DC Number</label>
            <input
              type="text"
              value={dcNumber}
              onChange={(e) => setDcNumber(e.target.value)}
              className="w-full border px-2 py-2 rounded"
              placeholder="Enter DC Number"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold">Date</label>
            <input
              type="date"
              value={date}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setDate(e.target.value)}
              className="w-full border px-2 py-2 rounded"
            />
          </div>

          {/* FIX 6: show Supplier dropdown OR location textbox OR nothing based on source */}
          <div>
            {source === "Supplier" && (
              <>
                <label className="block text-sm font-semibold">
                  Supplier
                </label>
                <select
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                  className="w-full border px-2 py-2 rounded"
                >
                  <option value="">Select</option>
                  {suppliersList.map((s) => (
                    <option
                      key={s.fld_supplierid ?? s.id ?? s.value}
                      value={s.fld_supplierid ?? s.id ?? s.value}
                    >
                      {s.fld_suppliername ?? s.name ?? s.text}
                    </option>
                  ))}
                </select>
              </>
            )}
            {source === "Own Work Shop" && (
              <>
                <label className="block text-sm font-semibold">Location</label>
                <input
                  type="text"
                  value={location_}
                  onChange={(e) => setLocation_(e.target.value)}
                  className="w-full border px-2 py-2 rounded"
                  placeholder="Enter location"
                />
              </>
            )}
            {source === "Scrap" && (
              <>
                <label className="block text-sm font-semibold text-gray-400">N/A</label>
                <input
                  type="text"
                  disabled
                  className="w-full border px-2 py-2 rounded bg-gray-50 text-gray-400"
                  placeholder="Not required for Scrap"
                />
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="mb-6 flex gap-4">
          <button
            onClick={() => toast.info("Entry page uses items from Listing.")}
            className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700"
          >
            <FaSearch /> Info
          </button>

          <button
            onClick={handleAdd}
            className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded"
          >
            Submit
          </button>
        </div>

        {/* Counter */}
        <div className="mb-3 text-sm text-gray-700">
          {selectedCount} selected for submit
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-gray-300">
            <thead className="bg-blue-800 text-white">
              <tr>
                <th className="p-2 border">S.No</th>
                <th className="p-2 border">Item Name</th>
                <th className="p-2 border">Item Code</th>
                <th className="p-2 border">Part No</th>
                <th className="p-2 border">Qty</th>
                {/* FIX 4: added UM Qty column so user can edit it, mirrors ASPX txt_Qty */}
                <th className="p-2 border">UM Qty</th>
                <th className="p-2 border">Remarks</th>
                <th className="p-2 border">Removed From</th>
                <th className="p-2 border">Select</th>
              </tr>
            </thead>
            <tbody>
              {selectedItems.map((item, idx) => (
                <tr key={item.fld_itemid || idx} className="text-center">
                  <td className="border p-2">{idx + 1}</td>
                  <td className="border p-2">{item.fld_itemname}</td>
                  <td className="border p-2">{item.fld_itemcode}</td>
                  <td className="border p-2">{item.fld_itempartno}</td>
                  <td className="border p-2">{item.fld_qty ?? item.qty ?? 1}</td>

                  {/* FIX 4: editable UM Qty — mirrors ASPX txt_Qty with FilteredTextBoxExtender */}
                  <td className="border p-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={qtyMap[item.fld_itemid] ?? ""}
                      onChange={(e) => handleQtyChange(item.fld_itemid, e.target.value)}
                      maxLength={9}
                      className="w-20 border px-2 py-1 rounded text-center"
                    />
                  </td>

                  <td className="border p-2">
                    <input
                      type="text"
                      value={remarksMap[item.fld_itemid] || ""}
                      onChange={(e) => handleRemarksChange(item.fld_itemid, e.target.value)}
                      className="w-full border px-2 py-1 rounded"
                    />
                  </td>

                  <td className="border p-2">
                    <input
                      type="text"
                      value={removedFromMap[item.fld_itemid] || ""}
                      onChange={(e) => handleRemovedFromChange(item.fld_itemid, e.target.value)}
                      className="w-full border px-2 py-1 rounded"
                    />
                  </td>

                  <td className="border p-2">
                    <input
                      type="checkbox"
                      checked={!!selected[item.fld_itemid]}
                      onChange={() => toggleRow(item.fld_itemid)}
                    />
                  </td>
                </tr>
              ))}
              {(!selectedItems || selectedItems.length === 0) && (
                <tr>
                  <td colSpan={9} className="p-6 text-center text-gray-500">
                    No items found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default UnderMaintenanceSparesEntry;